import { useState } from "react";
import { Alert, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { BucketAwtENUM, useUploadFile } from "@/shared";
import { useUpdateOrdenTrabajo } from "@/shared/actions/erp";
import { CustomPaperBox } from "@/shared/components/ui";
import { ScrollableDialogProps } from "@/shared/components/ui/Dialogs";
import { useUiStore } from "@/store/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
// Mismo uploader pulido que visitas (drag&drop + grilla) para estilo consistente.
import FotosVisitaMultiUploader from "../FotosVisitaMultiUploader";
import { FOTOS_INGRESO_REQUERIDAS, subirFotosTraslado } from "./fotos-traslado.utils";

// Traslados: exactamente 6 fotos de ingreso (mín = máx).
const MIN = FOTOS_INGRESO_REQUERIDAS;
const MAX = FOTOS_INGRESO_REQUERIDAS;

type IniciarTrasladoFotosModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ordenTrabajo?: OrdenTrabajo;
  location: { lat: number; lng: number } | null;
  username?: string;
};

/**
 * Modal para INICIAR el traslado: exige "Fotos de ingreso" (mín 2, máx 10) y
 * registra hora de inicio + coordenadas. Persiste `urls_fotos_ingreso`,
 * `fecha_hora_inicio_instalacion` y `coordenadas`.
 */
export default function IniciarTrasladoFotosModal({
  open,
  onClose,
  onSuccess,
  ordenTrabajo,
  location,
  username,
}: IniciarTrasladoFotosModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showError, setShowError] = useState(false);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const { handleUploadFile } = useUploadFile();
  const updateOT = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
    customSuccessMessage: "Traslado iniciado correctamente",
  });

  const PATH_BUCKET = `${BucketAwtENUM.IMAGE_INSTALACION}/${ordenTrabajo?.id_orden_trabajo}`;

  const handleConfirm = async () => {
    if (files.length < MIN) {
      setShowError(true);
      return;
    }
    if (!location) {
      toast.error("No se pudo obtener la ubicación actual.");
      return;
    }
    setIsGlobalLoading(true);
    try {
      const fotos = await subirFotosTraslado(files, PATH_BUCKET, handleUploadFile, "Ingreso");
      const historial = ordenTrabajo?.historial_cambios_coordenadas
        ? [...ordenTrabajo.historial_cambios_coordenadas]
        : [];
      historial.push({
        usuario: username || "",
        fecha_cambio: new Date().toISOString(),
        coordenada_anterior: ordenTrabajo?.coordenadas || "",
        coordenada_nueva: `${location.lat},${location.lng}`,
      });
      await updateOT.mutateAsync({
        id: ordenTrabajo?.id_orden_trabajo!,
        data: {
          fecha_hora_inicio_instalacion: dayjs(),
          coordenadas: `${location.lat},${location.lng}`,
          historial_cambios_coordenadas: historial,
          urls_fotos_ingreso: fotos,
        },
      });
      setFiles([]);
      setShowError(false);
      onClose();
      onSuccess();
    } catch (e) {
      console.error("Error al iniciar traslado", e);
      toast.error("Error al iniciar el traslado o subir las fotos.");
    } finally {
      setIsGlobalLoading(false);
    }
  };

  return (
    <ScrollableDialogProps
      open={open}
      title="Iniciar Traslado — Fotos de ingreso"
      minWidth="60%"
      contentNode={
        <CustomPaperBox sxPaper={{ p: 3 }}>
          <Grid item xs={12}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Se registrará la hora de inicio y las coordenadas
              {location ? ` (${location.lat.toFixed(6)}, ${location.lng.toFixed(6)})` : ""}.
              Sube <b>exactamente {MIN} fotos de ingreso</b>. Después, en la pestaña
              FOTOS, carga las 2 fotos de proceso (punto inicial y punto final).
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Agregar fotos de EPPs, coordenadas o equipos
            </Typography>
            <FotosVisitaMultiUploader
              files={files}
              setFiles={setFiles}
              min={MIN}
              max={MAX}
              label="Fotos de ingreso"
            />
            {showError && files.length < MIN && (
              <Typography color="error" fontWeight={700} sx={{ mt: 1 }}>
                Debes subir exactamente {MIN} fotos de ingreso para iniciar el traslado (llevas {files.length}).
              </Typography>
            )}
          </Grid>
        </CustomPaperBox>
      }
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmTextBtn="Iniciar traslado"
      confirmVariantBtn="outlined"
    />
  );
}
